import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Lock, CheckCircle } from "lucide-react";
import { resetPassword } from "@/services/authService";
import PasswordStrengthBar from "@/components/ui/PasswordStrengthBar";

const ResetPassword = ({ token }) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [passwordCheck, setPasswordCheck] = useState(null);
  const debounceRef = useRef(null);

  // Debounced password strength check
  useEffect(() => {
    if (!password || password.length < 4) {
      setPasswordCheck(null);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.post("/lmiga/user/check-password.json", {
          password,
        });
        setPasswordCheck(res.data?.data || null);
      } catch {
        setPasswordCheck(null);
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [password]);

  if (!token) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invalid Link</CardTitle>
            <CardDescription>
              This password reset link is missing a required token. Please
              request a new password reset.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err.message || "An error occurred. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
              <CardTitle>Password Reset Successfully</CardTitle>
            </div>
            <CardDescription>
              Your password has been updated. You can now log in with your new
              password.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-[400px] p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Set New Password</CardTitle>
          <CardDescription>Enter your new password below.</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="new-password">New Password:</label>
              <div className="relative">
                <div className="absolute left-3 top-3 text-gray-400 dark:text-gray-500">
                  <Lock className="h-4 w-4" />
                </div>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="Enter new password"
                  className="pl-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {passwordCheck && (
                <PasswordStrengthBar
                  strength={passwordCheck.strength}
                  valid={passwordCheck.valid}
                  details={passwordCheck.details}
                />
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="confirm-new-password">Confirm Password:</label>
              <div className="relative">
                <div className="absolute left-3 top-3 text-gray-400 dark:text-gray-500">
                  <Lock className="h-4 w-4" />
                </div>
                <Input
                  id="confirm-new-password"
                  type="password"
                  placeholder="Confirm new password"
                  className="pl-10"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              {password && confirmPassword && password !== confirmPassword && (
                <p className="text-red-500 dark:text-red-400 text-xs">
                  Passwords do not match
                </p>
              )}
            </div>
          </CardContent>

          <CardFooter>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Resetting..." : "Reset Password"}
            </Button>
          </CardFooter>
        </form>

        {error && (
          <div className="px-6 pb-6">
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ResetPassword;
